import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";

// Common Email Validation Rule
const emailValidation = Yup.string()
  .trim()
  .email("Please enter a valid email")
  .required("Please enter email")
  .matches(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/,
    "Please enter a valid email"
  );
// comman role management :
const roleValidation = Yup.string().required("Please select a role");

// Common full name Validation Rule
const fullNameValidation = Yup.string()
  .required("Please enter full name")
  .trim();
// Common Phone Number Validation Rule
const PhoneValidation = Yup.string()
  .required("Please enter phone number")
  .test("is-valid-phone", "Please enter valid phone number", (value) =>
    isValidPhoneNumber(value || "")
  );

// Common Password Validation Rule
const passwordValidation = Yup.string()
  .required("Please enter password")
  .min(8, "Password cannot be less than 8 characters")
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
    "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
  );

const firstNameValidation = Yup.string()
  .required("Please enter first name")
  .trim();
const lastNameValidation = Yup.string()
  .required("Please enter last name")
  .trim();

const linkRegex =
  /^(https?:\/\/)?((localhost|(\d{1,3}\.){3}\d{1,3})|([\w-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[^\s]*)?$/;

export const applyJobSchema = Yup.object().shape({
  full_name: Yup.string().required("Full Name cannot be empty").trim(),
  phone_number: Yup.string()
    .required("Phone Number cannot be empty")
    .test("is-valid-phone", "Please enter valid phone number", (value) =>
      isValidPhoneNumber(value || "")
    ),
  expected_salary: Yup.string()
    .required("Expected Salary cannot be empty")
    .test(
      "non-negative",
      "Expected Salary must be 0 or greater",
      (value) => {
        if (value === undefined || value === null || value === "") return true;
        const num = Number(value);
        return !Number.isNaN(num) && num >= 0;
      }
    )
    .matches(/^\d+(\.\d+)?$/, "Expected Salary must be a number"),
  work_type: Yup.string().required("Please select work type"),
  current_position: Yup.string()
    .required("Please enter your current position")
    .trim(),
  linkedin_url: Yup.string()
    .required("Linkedin Profile Link cannot be empty")
    .matches(linkRegex, "Linkedin Profile Link is not valid"),
  portfolio_url: Yup.string()
    .required("Portfolio Link cannot be empty")
    .matches(linkRegex, "Portfolio Link is not valid"),
  cover_letter: Yup.string()
    .required("Please enter cover letter")
    .trim(),
  resume_files: Yup.array().min(1, "Please select resume"),
});

export const registerSchema = Yup.object().shape({
  email: emailValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  linkedin_url: Yup.string().required("Please enter linkedin profile").trim(),
  phone_number: PhoneValidation,
  role: Yup.string().required("Please select current role"),
  source: Yup.string().required("Please select one"),
});

export const contactUsSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  address: Yup.string().required("Please enter address").trim(),
  subject: Yup.string().required("Please select what this inquiry is about"),
  message: Yup.string().required("Please enter message").trim(),
});

export const createCoachPasswordSchema = Yup.object().shape({
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
})

export const signInSchema = Yup.object().shape({
  email: emailValidation,
  password: passwordValidation,
});
export const adminSignInSchema = Yup.object().shape({
  email: emailValidation,
  password: passwordValidation,
});

export const forgotPasswordSchema = Yup.object().shape({
  email: emailValidation,
});

export const resetPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("Please enter password")
    .min(8, "Password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
    ),
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("newPassword"), null], "Your password must match"),
});

export const changePasswordSchema = Yup.object().shape({
  current_password: Yup.string()
    .required("Please enter current password")
    .min(8, "Current password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Please enter a valid password"
    ),
  new_password: Yup.string()
    .required("Please enter new password")
    .min(8, "New password cannot be less than 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%&'*+-.,:;<=>?^_`{|}~])/,
      "Strong passwords require at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character."
    ),
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("new_password"), null], "Your password must match"),
});

export const step1Schema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
});

export const step2Schema = Yup.object().shape({
  category_id: Yup.string().required("Please select a coaching category"),
  format: Yup.string().required("Please select a coaching format"),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),
});

export const step3Schema = Yup.object().shape({
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  // tagline: Yup.string()
  //   .required("Please enter a tagline")
  //   .trim()
  //   .test(
  //     "min-words",
  //     "Tagline must be at least 50 words",
  //     (value) => {
  //       if (!value) return false; // required will handle empty
  //       const wordCount = value.trim().split(/\s+/).length; // count words
  //       return wordCount >= 50;
  //     }
  //   ),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  profile_image: Yup.mixed()
    .required("Please upload your profile image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  cv: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),

  certificate: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),
});

export const step1BootCampSchema = Yup.object().shape({
  header_title: Yup.string().required("Please enter bootcamp heading").trim(),
  header_sub_title: Yup.string()
    .required("Please enter bootcamp sub heading")
    .trim(),
  start_date: Yup.date()
    .required("Please select a Bootcamp start date")
    .typeError("Please enter a valid date"),

  end_date: Yup.date()
    .required("Please select a Bootcamp end date")
    .test(
      "is-same-or-after-start",
      "End date must be the same or after the start date",
      function (value) {
        const { start_date } = this.parent;
        return value && start_date && value >= start_date;
      }
    )
    .typeError("Please enter a valid date"),
  location: Yup.string().required("Please enter Bootcamp location").trim(),
  header_description: Yup.string()
    .required("Please enter Bootcamp description")
    .trim(),
});

export const step3BootCampSchema = Yup.object().shape({
  footer_title: Yup.string()
    .required("Please enter Bootcamp sub heading")
    .trim(),
  footer_sub_title: Yup.string()
    .required("Please enter Bootcamp sub title")
    .trim(),
  footer_description: Yup.string()
    .required("Please enter Bootcamp sub description")
    .trim(),
  footer_image: Yup.mixed()
    .required("Please upload image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  brochure: Yup.mixed()
    .required("Please upload image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
  header_image: Yup.mixed()
    .required("Please upload your Bootcamp image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      const validFileTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return validFileTypes.includes(value.type);
    }),
});

export const bootcampPurchaseSchema = Yup.object().shape({
  email: emailValidation,
  phone_number: PhoneValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
});

export const bootcampWaittingListSchema = Yup.object().shape({
  email: emailValidation,
  phone_number: PhoneValidation,
  name: Yup.string().trim().required("Please enter name"),
  notes: Yup.string().trim().optional(),
});

export const becomeCoachSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  category_id: Yup.string().required("Please select a coaching category"),
  format: Yup.string().required("Please select a coaching format"),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  profile_image: Yup.mixed()
    .required("Please upload your profile photo")
    .test("fileType", "Only JPG, JPEG, PNG files are allowed", (value) => {
      if (!value) return false;
      return ["image/jpg", "image/jpeg", "image/png"].includes(value.type);
    })
    .test("fileSize", "File size must be less than 2MB", (value) => {
      if (!value) return false;
      return value.size <= 2 * 1024 * 1024;
    }),
  cv: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),

  certificate: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return false;

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => allowed.includes(file.type));
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return false;

      return files.every((file) => file.size <= 2 * 1024 * 1024);
    }),
});

export const updateProfileSchema = Yup.object().shape({
  email: emailValidation,
  full_name: fullNameValidation,
  phone_number: PhoneValidation,
  bio: Yup.string().required("Please enter bio").trim(),
  reason: Yup.string().required("Please enter reason").trim(),
  tagline: Yup.string().required("Please enter a tagline").trim(),
  year_of_experience: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter your years of experience")
    .min(0, "Experience cannot be negative")
    .max(100, "Please enter a realistic experience (max 100 years)"),

  gender: Yup.string().nullable().trim(),

  dob: Yup.string().nullable().trim(),

  language: Yup.string().nullable().trim(),

  address_1: Yup.string().nullable().trim(),

  address_2: Yup.string().nullable().trim(),

  city: Yup.string().nullable().trim(),

  country: Yup.string().nullable().trim(),

  state: Yup.string().nullable().trim(),

  zip_code: Yup.string().nullable().trim(),
});

export const registerFindAJobSchema = Yup.object().shape({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  email: emailValidation,
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
});

export const simpleSignupSchema = Yup.object().shape({
  full_name: fullNameValidation,
  email: emailValidation,
  password: passwordValidation,
  user_type: Yup.string().required("Please select a user type"),
  consent_accuracy: Yup.boolean()
    .oneOf([true], "Please confirm the information is accurate")
    .required("Please confirm the information is accurate"),
  consent_contact: Yup.boolean()
    .oneOf([true], "Please agree to be contacted by NUVIO")
    .required("Please agree to be contacted by NUVIO"),
});

export const freelancerSignupSchema = Yup.object().shape({
  full_name: fullNameValidation,
  email: emailValidation,
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
});

export const createOfferSchema = Yup.object().shape({
  offer_title: Yup.string().required("Please enter offer title").trim(),
  purpose: Yup.string().required("Please enter purpose").trim(),
  offer_type: Yup.string().required("Please select offer type").trim(),
  offer_format: Yup.string().required("Please select offer format").trim(),
  duration: Yup.string().required("Please select duration").trim(),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Please enter price")
    .positive("Price must be greater than zero")
    .min(1, "Price must be at least 1")
    .integer("Price must be a valid number"),
  additional_notes: Yup.string().required("Please select duration").trim(),
});

export const profileStep1Schema = Yup.object().shape({
  phone_number: PhoneValidation,
  country: Yup.string().required("Please enter country").trim(),
  city: Yup.string().required("Please enter city").trim(),
  linkedin_profile: Yup.string()
    .required("Please enter LinkedIn profile")
    .trim(),
  portfolio_website: Yup.string().optional()
    // .required("Please enter portfolio")
    .trim(),
  currency: Yup.string()
    .required("Please select currency"),
  bio: Yup.string().required("Please enter about me").trim(),
});

export const profileStep2Schema = Yup.object().shape({
  primary_role: Yup.string().required("Please select a primary role").trim(),
  years_of_experience: Yup.string()
    .required("Please select years of experience")
    .trim(),
  key_skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  tools: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one tool")
    .required("Please add at least one tool"),
  availability: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one availability option")
    .max(3, "You can select up to 3 availability options")
    .required("Please select availability"),
  hourly_rate: Yup.string().nullable().trim(),
  preferred_project_type: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one project type")
    .required("Please select at least one project type"),
});

export const certificateEntrySchema = Yup.object().shape({
  title: Yup.string().required("Please enter certificate title").trim(),
  location: Yup.string().required("Please enter location").trim(),
  start_date: Yup.date()
    .typeError("Please enter a valid start date")
    .required("Please enter start date"),
  end_date: Yup.date()
    .typeError("Please enter a valid end date")
    .required("Please enter end date")
    .test(
      "is-same-or-after-start",
      "End date must be the same or after the start date",
      function (value) {
        const { start_date } = this.parent;
        if (!value || !start_date) return false;
        return value >= start_date;
      }
    ),
});

export const certificateListSchema = Yup.array()
  .of(certificateEntrySchema)
  .min(1, "Please add at least one certificate")
  .required("Please add at least one certificate");

export const certificateModalSchema = Yup.object().shape({
  certificates: certificateListSchema,
});

export const profileStep3Schema = Yup.object().shape({
  cv: Yup.array()
    .required("Please upload your CV (PDF only)")
    .min(1, "Please upload your CV (PDF only)")
    .test("fileType", "Only PDF files are accepted", (files) => {
      if (!files || files.length === 0) return false;
      return files.every((file) => {
        if (file instanceof File) {
          return file.type === "application/pdf";
        }
        if (typeof file === "string") {
          return file.toLowerCase().includes(".pdf");
        }
        if (file && typeof file === "object") {
          const name = (file.name || "").toLowerCase();
          const preview = (file.preview || file.url || "").toLowerCase();
          const type = (file.type || "").toLowerCase();
          return (
            type.includes("pdf") ||
            name.includes(".pdf") ||
            preview.includes(".pdf")
          );
        }
        return false;
      });
    }),
  certificates: certificateListSchema,
  experiences: Yup.array()
    .of(
      Yup.object().shape({
        job_title: Yup.string().required("Please enter job title").trim(),
        company_name: Yup.string().required("Please enter company name").trim(),
        location: Yup.string().required("Please enter location").trim(),
        experience_images: Yup.mixed()
          .required("Please upload company image")
          .test("fileType", "Please upload a valid image", (value) => {
            if (!value) return false;
            const item = Array.isArray(value) ? value[0] : value;
            if (!item) return false;
            if (item instanceof File) {
              return (item.type || "").startsWith("image/");
            }
            if (typeof item === "string") return true;
            if (item?.file instanceof File) {
              return (item.file.type || "").startsWith("image/");
            }
            const type = (item?.type || "").toLowerCase();
            const name = (item?.name || "").toLowerCase();
            const preview = (item?.preview || item?.url || "").toLowerCase();
            return (
              type.startsWith("image/") ||
              name.includes(".jpg") ||
              name.includes(".jpeg") ||
              name.includes(".png") ||
              name.includes(".webp") ||
              preview.includes(".jpg") ||
              preview.includes(".jpeg") ||
              preview.includes(".png") ||
              preview.includes(".webp")
            );
          }),
        job_description: Yup.string()
          .required("Please enter work experience")
          .trim(),
        start_date: Yup.date()
          .typeError("Please enter a valid start date")
          .required("Please enter start date"),
        end_date: Yup.date()
          .nullable()
          .typeError("Please enter a valid end date")
          .when("currently_working", {
            is: true,
            then: (schema) => schema.nullable(),
            otherwise: (schema) =>
              schema.required("Please enter end date"),
          }),
        currently_working: Yup.boolean(),
        link: Yup.string()
          .nullable()
          .notRequired()
          .test("is-valid-url", "Please enter a valid link", (value) => {
            if (!value) return true;
            return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(
              value
            );
          }),
      })
    )
    .nullable(),
  portfolio_links: Yup.array()
    .of(
      Yup.string()
        .nullable()
        .test("is-valid-url", "Please enter a valid link", (value) => {
          if (!value) return true;
          return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(
            value
          );
        })
    )
    .nullable(),
  work_samples: Yup.array()
    .test("fileType", "Only ZIP files are allowed", (files) => {
      if (!files || files.length === 0) return true;
      const allowed = ["application/zip", "application/x-zip-compressed"];
      return files.every((file) => {
        if (file instanceof File) {
          return allowed.includes(file.type);
        }
        if (typeof file === "string") {
          return file.toLowerCase().includes(".zip");
        }
        if (file && typeof file === "object") {
          const name = (file.name || "").toLowerCase();
          const preview = (file.preview || file.url || "").toLowerCase();
          const projectUrl = (file.project_url || "").toLowerCase();
          const type = (file.type || "").toLowerCase();
          return (
            type.includes("zip") ||
            type === "zip" ||
            name.includes(".zip") ||
            preview.includes(".zip") ||
            projectUrl.includes(".zip")
          );
        }
        return false;
      });
    })
    .test("fileSize", "Each file must be less than 20 MB", (files) => {
      if (!files || files.length === 0) return true;
      return files.every((file) => {
        if (file instanceof File) {
          return file.size <= 20 * 1024 * 1024;
        }
        return true;
      });
    }),
});

export const profileStep4Schema = Yup.object().shape({
  join_reason: Yup.string()
    .required("Please share why you want to join NUVIO")
    .trim(),
  interested_clients: Yup.string()
    .required("Please share the clients you are interested in")
    .trim(),
  heard_about: Yup.string().required("Please select how you heard about NUVIO"),
  consent_accuracy: Yup.boolean()
    .oneOf([true], "Please confirm the information is accurate")
    .required("Please confirm the information is accurate"),
  consent_contact: Yup.boolean()
    .oneOf([true], "Please agree to be contacted by NUVIO")
    .required("Please agree to be contacted by NUVIO"),
});

export const freelancerEditProfileSchema = Yup.object().shape({
  full_name: Yup.string().required("Please enter full name").trim(),
  phone_number: PhoneValidation,
  country: Yup.string().required("Please enter country").trim(),
  city: Yup.string().required("Please enter city").trim(),
  linkedin_profile: Yup.string()
    .required("Please enter LinkedIn profile")
    .matches(linkRegex, "Please enter a valid link"),
  portfolio_website: Yup.string()
    .nullable()
    .notRequired()
    .test("is-valid-url", "Please enter a valid link", (value) => {
      if (!value) return true;
      return linkRegex.test(value);
    }),
  bio: Yup.string().required("Please enter bio").trim(),
  primary_role: Yup.string().required("Please select a primary role").trim(),
  other_primary_role: Yup.string().when("primary_role", {
    is: "Other",
    then: (schema) => schema.required("Please enter primary role").trim(),
    otherwise: (schema) => schema.trim().nullable(),
  }),
  years_of_experience: Yup.string()
    .required("Please select years of experience")
    .trim(),
  availability: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one availability option")
    .max(3, "You can select up to 3 availability options")
    .required("Please select availability"),
  hourly_rate: Yup.string().nullable().trim(),
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  tools: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one tool")
    .required("Please add at least one tool"),
  preferred_project_type: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one project type")
    .required("Please select at least one project type"),
  profile_image: Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value || typeof value === "string") return true;
      if (value instanceof File) {
        return (value.type || "").startsWith("image/");
      }
      return true;
    }),
  cv: Yup.array()
    .required("Please upload your CV (PDF only)")
    .min(1, "Please upload your CV (PDF only)"),
  certificates: certificateListSchema,
  experiences: Yup.array()
    .of(
      Yup.object().shape({
        job_title: Yup.string().required("Please enter job title").trim(),
        company_name: Yup.string().required("Please enter company name").trim(),
        location: Yup.string().required("Please enter location").trim(),
        experience_images: Yup.mixed()
          .required("Please upload company image")
          .test("fileType", "Please upload a valid image", (value) => {
            if (!value) return false;
            const item = Array.isArray(value) ? value[0] : value;
            if (!item) return false;
            if (item instanceof File) {
              return (item.type || "").startsWith("image/");
            }
            if (typeof item === "string") return true;
            if (item?.file instanceof File) {
              return (item.file.type || "").startsWith("image/");
            }
            const type = (item?.type || "").toLowerCase();
            const name = (item?.name || "").toLowerCase();
            const preview = (item?.preview || item?.url || "").toLowerCase();
            return (
              type.startsWith("image/") ||
              name.includes(".jpg") ||
              name.includes(".jpeg") ||
              name.includes(".png") ||
              name.includes(".webp") ||
              preview.includes(".jpg") ||
              preview.includes(".jpeg") ||
              preview.includes(".png") ||
              preview.includes(".webp")
            );
          }),
        job_description: Yup.string()
          .required("Please enter work experience")
          .trim(),
        start_date: Yup.date()
          .typeError("Please enter a valid start date")
          .required("Please enter start date"),
        end_date: Yup.date()
          .nullable()
          .typeError("Please enter a valid end date")
          .when("currently_working", {
            is: true,
            then: (schema) => schema.nullable(),
            otherwise: (schema) => schema.required("Please enter end date"),
          }),
        currently_working: Yup.boolean(),
      })
    )
    .min(1, "Please add at least one experience")
    .required("Please add at least one experience"),
  work_samples: Yup.array().nullable(),
});

export const experienceModalSchema = Yup.object().shape({
  job_title: Yup.string().required("Please enter job title").trim(),
  company_name: Yup.string().required("Please enter company name").trim(),
  location: Yup.string().required("Please enter location").trim(),
  experience_images: Yup.mixed()
    .required("Please upload company image")
    .test("fileType", "Please upload a valid image", (value) => {
      if (!value) return false;
      const item = Array.isArray(value) ? value[0] : value;
      if (!item) return false;
      if (item instanceof File) {
        return (item.type || "").startsWith("image/");
      }
      if (typeof item === "string") return true;
      if (item?.file instanceof File) {
        return (item.file.type || "").startsWith("image/");
      }
      const type = (item?.type || "").toLowerCase();
      const name = (item?.name || "").toLowerCase();
      const preview = (item?.preview || item?.url || "").toLowerCase();
      return (
        type.startsWith("image/") ||
        name.includes(".jpg") ||
        name.includes(".jpeg") ||
        name.includes(".png") ||
        name.includes(".webp") ||
        preview.includes(".jpg") ||
        preview.includes(".jpeg") ||
        preview.includes(".png") ||
        preview.includes(".webp")
      );
    }),
  job_description: Yup.string()
    .required("Please enter work experience")
    .trim(),
  start_date: Yup.date()
    .typeError("Please enter a valid start date")
    .required("Please enter start date"),
  end_date: Yup.date()
    .nullable()
    .typeError("Please enter a valid end date")
    .when("currently_working", {
      is: true,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required("Please enter end date"),
    }),
  currently_working: Yup.boolean(),
});

export const professionalInfoSchema = Yup.object().shape({
  primary_role: Yup.string().required("Please select a primary role").trim(),
  other_primary_role: Yup.string().when("primary_role", {
    is: "Other",
    then: (schema) => schema.required("Please enter primary role").trim(),
    otherwise: (schema) => schema.trim().nullable(),
  }),
  availability: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one availability option")
    .max(3, "You can select up to 3 availability options")
    .required("Please select availability"),
  linkedin_profile: Yup.string()
    .required("Please enter LinkedIn profile")
    .matches(linkRegex, "Please enter a valid link"),
  website_or_portfolio: Yup.string()
    .nullable()
    .notRequired()
    .test("is-valid-url", "Please enter a valid link", (value) => {
      if (!value) return true;
      return linkRegex.test(value);
    }),
  hourly_rate: Yup.string().nullable().trim(),
});

export const toolsModalSchema = Yup.object().shape({
  tools: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one tool")
    .required("Please add at least one tool"),
});

export const skillsModalSchema = Yup.object().shape({
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
});

export const workSamplesModalSchema = Yup.object().shape({
  work_samples: Yup.array()
    .test("fileType", "Only ZIP files are allowed", (files) => {
      if (!files || files.length === 0) return true;
      const allowed = ["application/zip", "application/x-zip-compressed"];
      return files.every((file) => {
        if (file instanceof File) {
          return allowed.includes(file.type);
        }
        if (typeof file === "string") {
          return file.toLowerCase().includes(".zip");
        }
        if (file && typeof file === "object") {
          const name = (file.name || "").toLowerCase();
          const preview = (file.preview || file.url || "").toLowerCase();
          const projectUrl = (file.project_url || "").toLowerCase();
          const type = (file.type || "").toLowerCase();
          return (
            type.includes("zip") ||
            type === "zip" ||
            name.includes(".zip") ||
            preview.includes(".zip") ||
            projectUrl.includes(".zip")
          );
        }
        return false;
      });
    })
    .test("fileSize", "Each file must be less than 20 MB", (files) => {
      if (!files || files.length === 0) return true;
      return files.every((file) => {
        if (file instanceof File) {
          return file.size <= 20 * 1024 * 1024;
        }
        return true;
      });
    }),
});

export const documentsModalSchema = Yup.object().shape({
  cv: Yup.array()
    .required("Please upload your CV (PDF only)")
    .min(1, "Please upload your CV (PDF only)")
    .test("fileType", "Only PDF files are accepted", (files) => {
      if (!files || files.length === 0) return false;
      return files.every((file) => {
        if (file instanceof File) {
          return file.type === "application/pdf";
        }
        if (typeof file === "string") {
          return file.toLowerCase().includes(".pdf");
        }
        if (file && typeof file === "object") {
          const name = (file.name || "").toLowerCase();
          const preview = (file.preview || file.url || "").toLowerCase();
          const type = (file.type || "").toLowerCase();
          return (
            type.includes("pdf") ||
            name.includes(".pdf") ||
            preview.includes(".pdf")
          );
        }
        return false;
      });
    }),
  certificates: certificateListSchema,
});

export const createContentSchema = Yup.object().shape({
  title: Yup.string().required("Please enter title").trim(),
  content_type: Yup.string().required("Please select content type"),
  visibility: Yup.string().required("Please select visibility"),
  status: Yup.string().required("Please select status"),
  short_description: Yup.string().required("Please enter short description"),

  files: Yup.array()
    .required("Please upload at least one file")
    .min(1, "Please upload at least one file")

    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return true; // let required/min handle it

      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing backend value as string (URL) → always ok
        if (typeof file === "string") return true;

        // 2) New upload (File object)
        if (file instanceof File) {
          return allowed.includes(file.type);
        }

        // 3) Existing backend object: { content_file_id, file: "url", ... }
        if (typeof file === "object" && (file.file || file.url)) {
          return true; // trust backend, already uploaded
        }

        return false;
      });
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return true;

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing string URL → skip size check
        if (typeof file === "string") return true;

        // 2) New upload (File object) → check size
        if (file instanceof File) {
          return file.size <= 2 * 1024 * 1024;
        }

        // 3) Existing backend object (no size property) → skip size check
        if (typeof file === "object" && (file.file || file.url)) {
          return true;
        }

        return true;
      });
    }),
  image: Yup.array()
    .required("Please upload image")
    .min(1, "Please upload at least one image")
    .test("fileType", "Only PDF or Image files are allowed", (files) => {
      if (!files || files.length === 0) return true; // let required/min handle it
      const allowed = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      return files.every((file) => {
        if (!file) return false;

        // 1) Existing backend value as string (URL) → always ok
        if (typeof file === "string") return true;

        // 2) New upload (File object)
        if (file instanceof File) {
          return allowed.includes(file.type);
        }

        // 3) Existing backend object: { content_file_id, file: "url", ... }
        if (typeof file === "object" && (file.file || file.url)) {
          return true; // trust backend, already uploaded
        }

        return false;
      });
    })
    .test("fileSize", "Each file must be less than 2 MB", (files) => {
      if (!files || files.length === 0) return true;

      return files.every((file) => {
        if (!file) return false;

        // 1) Existing string URL → skip size check
        if (typeof file === "string") return true;

        // 2) New upload (File object) → check size
        if (file instanceof File) {
          return file.size <= 2 * 1024 * 1024;
        }

        // 3) Existing backend object (no size property) → skip size check
        if (typeof file === "object" && (file.file || file.url)) {
          return true;
        }

        return true;
      });
    }),
});
export const updateFreelancerProfileSchema = Yup.object().shape({
  email: emailValidation,
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  talent_role: Yup.string().required("Please enter role").trim(),
  location: Yup.string().required("Please enter location").trim(),
  bio: Yup.string().required("Please enter bio").trim(),
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  // language: Yup.string().required("Please enter language").trim(),

  website_or_portfolio: Yup.string().optional()
    // .required("Please enter Portfolio")
    .trim(),
  dob: Yup.string().nullable().trim(),
  gender: Yup.string().nullable().trim(),
  phone_number: Yup.string().required("Please enter phone number"),
  city: Yup.string().nullable().trim(),
  country: Yup.string().nullable().trim(),
  state: Yup.string().nullable().trim(),
  address_1: Yup.string().nullable().trim(),
  address_2: Yup.string().nullable().trim(),
  zip_code: Yup.string().nullable().trim(),
  job_type: Yup.string().required("Please select a job type"),
  search_status: Yup.string().required("Please select job status"),
  salary_preference: Yup.string().required("Salary preference is required"),
});

export const hireTalentCompanyDetailSchema = Yup.object().shape({
  company_name: Yup.string().required("Please enter company name").trim(),
  company_description: Yup.string()
    .required("Please enter company description")
    .trim(),
  industry: Yup.string().required("Please enter industry").trim(),
  country: Yup.string().required("Please enter country").trim(),
  city: Yup.string().required("Please enter city").trim(),
  company_website: Yup.string()
    .matches(linkRegex, "Please enter a valid link")
    .required("Please enter company website"),
  company_size: Yup.string().required("Please select company size"),
});

export const hireTalentSignupStep1Schema = Yup.object().shape({
  full_name: fullNameValidation,
  email: emailValidation,
  password: passwordValidation,
  confirm_password: Yup.string()
    .required("Please enter confirm password")
    .oneOf([Yup.ref("password"), null], "Your password must match"),
  company_name: Yup.string().required("Please enter company name").trim(),
  industry: Yup.string().required("Please select industry").trim(),
  company_size: Yup.string().required("Please select company size"),
  company_website: Yup.string()
    .matches(linkRegex, "Please enter a valid link")
    .required("Please enter company website"),
  linkedin_company: Yup.string()
    .matches(linkRegex, "Please enter a valid link")
    .required("Please enter LinkedIn company page")
    .trim(),
  country: Yup.string().required("Please select country").trim(),
  city: Yup.string().required("Please enter city").trim(),
  company_description: Yup.string()
    .required("Please enter company description")
    .trim(),
});

export const hireTalentSignupStep2Schema = Yup.object().shape({
  primary_contact_name: Yup.string().required("Please enter full name").trim(),
  primary_contact_title: Yup.string().required("Please enter job title").trim(),
  primary_contact_email: emailValidation,
  primary_contact_phone: PhoneValidation,
  secondary_contact_name: Yup.string().required("Please enter full name").trim(),
  secondary_contact_title: Yup.string().required("Please enter job title").trim(),
  secondary_contact_email: emailValidation,
  secondary_contact_phone: PhoneValidation,
});

export const hireTalentSignupStep3Schema = Yup.object().shape({
  talent_types: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one talent type")
    .required("Please select at least one talent type"),
  talent_other: Yup.string().when("talent_types", {
    is: (val) => Array.isArray(val) && val.includes("Other"),
    then: (schema) => schema.required("Please specify other talent type").trim(),
    otherwise: (schema) => schema.trim(),
  }),
  engagement_types: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one engagement type")
    .required("Please select at least one engagement type"),
  project_title: Yup.string().required("Please enter project title").trim(),
  role_in_project: Yup.string().required("Please enter role in project").trim(),
  project_start_date: Yup.date()
    .typeError("Please enter a valid start date")
    .test("not-in-past", "Past date is not allowed", (value) => {
      if (!value) return true;
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    })
    .required("Please enter start date"),
  team_member_required: Yup.string()
    .required("Please enter team member")
    .trim()
    .matches(/^[0-9]+$/, "Only numbers are allowed"),
  estimated_budget: Yup.string().required("Please enter budget").trim(),
  project_duration: Yup.string().required("Please select duration").trim(),
  project_skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  project_description: Yup.string()
    .required("Please enter project description")
    .trim(),
  preferred_timezone: Yup.string().required("Please select time zone").trim(),
  communication_tools: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please select at least one tool")
    .required("Please select at least one tool"),
  communication_other: Yup.string().when("communication_tools", {
    is: (val) => Array.isArray(val) && val.includes("Other"),
    then: (schema) => schema.required("Please specify other tool").trim(),
    otherwise: (schema) => schema.trim(),
  }),
  collaboration_type: Yup.string()
    .required("Please describe collaboration")
    .trim(),
  consent_accuracy: Yup.boolean()
    .oneOf([true], "Please confirm the information is accurate")
    .required("Please confirm the information is accurate"),
  consent_contact: Yup.boolean()
    .oneOf([true], "Please agree to be contacted by NUVIO")
    .required("Please agree to be contacted by NUVIO"),
});

export const reserveYourStopSchema = Yup.object().shape({
  email: emailValidation,
  name: Yup.string().trim().required("Please enter name"),
  notes: Yup.string().trim().optional(),
});

export const jobPostSchema = Yup.object().shape({
  job_title: Yup.string().trim().required("Please enter job title"),
  location: Yup.string().trim().required("Please enter location"),
  industry: Yup.string().trim().required("Please enter industry"),
  category: Yup.string().trim().required("Please enter category"),
  candidate_location: Yup.string().trim().required("Please select candidate location"),
  candidate_availability: Yup.string().trim().required("Please select candidate availability"),
  license: Yup.string().trim().required("Please select license"),
  job_description: Yup.string().trim().required("Please enter job description"),
  about_role: Yup.string().trim().required("Please enter about role"),
  responsibility: Yup.string().trim().required("Please enter responsibility"),
  education: Yup.string().trim().required("Please enter education"),
  years_of_experience: Yup.number()
    .typeError("Years of experience must be a number")
    .required("Please enter years of experience")
    .moreThan(0, "Years of experience must be greater than 0"),
  minimum_education: Yup.string().trim().required("Please select minimum education"),
  salary_currency: Yup.string().trim().required("Please select salary currency"),
  min_salary: Yup.number()
    .typeError("Min salary must be a number")
    .required("Please enter min salary")
    .moreThan(0, "Min salary must be greater than 0"),
  max_salary: Yup.number()
    .typeError("Max salary must be a number")
    .required("Please enter max salary")
    .moreThan(
      Yup.ref("min_salary"),
      "Max salary must be greater than min salary"
    ),
  start_date: Yup.date()
    .typeError("Please enter valid start date")
    .required("Please enter start date"),
  end_date: Yup.date()
    .typeError("Please enter valid end date")
    .nullable()
    .min(
      Yup.ref("start_date"),
      "End date must be after start date"
    ),
  skills: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  qualification: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one qualification")
    .required("Please add at least one qualification"),
  requirement: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one requirement")
    .required("Please add at least one requirement"),
});

export const addEditProjectSchema = Yup.object().shape({
  project_title: Yup.string().trim().required("Please enter project title"),
  project_description: Yup.string().trim().required("Please enter project description"),
  availability: Yup.string().trim().required("Please select availability"),
  // project_categories: Yup.string().required("Please enter project categories"),
  role_in_project: Yup.string().trim().required("Please enter project role"),
  project_duration: Yup.string()
    .oneOf(
      ["Less than 1 month", "1–3 months", "3–6 months", "6+ months"],
      "Please select project duration"
    )
    .required("Please select project duration"),
  budget: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .typeError("Budget must be a number")
    .required("Please enter budget")
    .moreThan(0, "Budget must be greater than 0"),
  start_date: Yup.date()
    .typeError("Please select valid start date")
    .required("Please select start date"),
  project_categories: Yup.array()
    .of(Yup.string().trim().min(1, "Cannot be empty"))
    .min(1, "Please add at least one skill")
    .required("Please add at least one skill"),
  team_member_required: Yup.string()
    .trim()
    .required("Please enter team member")
    .matches(/^[0-9]+$/, "Only numbers are allowed")
    .test(
      "not-zero",
      "Team member must be greater than 0",
      (value) => Number(value) > 0
    ),
});

export const uploadDeliveablesSchema = Yup.object().shape({
  files: Yup.array()
    .required("Please upload at least 1 project files (ZIP)")
    .min(1, "Please upload at least 1 project files (ZIP)")
    .test("fileType", "Only ZIP files are allowed", (files) => {
      if (!files || files.length === 0) return false;
      return files.every(
        (file) =>
          file.type === "application/zip" ||
          file.name.toLowerCase().endsWith(".zip")
      );
    }),
  // .test("fileSize", "Each file must be less than 20 MB", (files) => {
  //   if (!files || files.length === 0) return false;
  //   return files.every((file) => file.size <= 20 * 1024 * 1024);
  // }),
  link: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "is-valid-url",
      "Please enter a valid link",
      (value) => {
        if (!value) return true;
        return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value);
      }
    ),
  comment: Yup.string()
    .nullable()
    .notRequired(),
});

export const sendMessageSchema = Yup.object().shape({
  text: Yup.string()
    .required("Please enter a message")
    .trim(),
});
